import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import { useLocation, useNavigate } from 'react-router-dom';
import TableHeader from '../common/table_header';
import routes from '../../routes';
import { getHeaders, handleErrorResponse } from '../../lib/requests';
import PaginationControl from '../common/pagination_control';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import GeneralButton from '../common/button_general';
import GeneralizedTableRow from '../common/table_row';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import NoResultsFound from '../common/noresultsfound';
import ItemFilterComponent from '../common/filtering/item_filter_list';

function ItemList() {
  const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting("asc_name");

  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const searchQueryParam = queryParams.get('search');
  const typeQueryParam = queryParams.get('type');
  const categoryQueryParam = queryParams.get('category');
  const dietaryRestrictionQueryParam = queryParams.get('dietary_restriction_type');

  const currentRoute = routes.find(route => route.path === location.pathname);
  const routePageTitle = currentRoute ? currentRoute.pageTitle : '';

  const [searchQuery] = useState(searchQueryParam || '');
  const [type, setType] = useState(typeQueryParam || '');
  const [category, setCategory] = useState(categoryQueryParam || '');
  const [dietaryRestriction, setDietaryRestriction] = useState(dietaryRestrictionQueryParam || '');

  useEffect(() => {
    const fetchItems = async () => {
      if (!pageSize) return;
      setIsLoading(true);
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/items`, {
          headers: getHeaders(apiToken),
          params: { 
            page, 
            pageSize, 
            ...(sortBy && { order_by: sortBy }), 
            ...(searchQuery && { search: searchQuery }),
            ...(type && { type }), 
            ...(category && { category }), 
            ...(dietaryRestriction && { dietary_restriction_type: dietaryRestriction })
          },
        });
        setTotalPages(response.data.totalPages);
        setTotalRecords(response.data.total);
        setItems(response.data.data);
      } catch (error) {
        handleErrorResponse(error, navigate, setShowError, setErrorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, [page, pageSize, sortBy, searchQuery, type, category, dietaryRestriction]);

  const handleDelete = async (item) => {
    const confirmed = window.confirm(`Are you sure you want to delete item ${item.name}?`);
    if (!confirmed) return;
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      await axios.delete(`${apiUrl}/items/${item.item_id}`, {
        headers: getHeaders(apiToken)
      });
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    }
  };

  const handleExport = async () => {
    setIsLoading(true);
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;

      // Fetch all items by making multiple paginated requests
      let allItems = [];
      let currentPage = 1;
      let totalPages = 1;
      const maxPageSize = 1000; // API maximum

      do {
        const response = await axios.get(`${apiUrl}/items`, {
          headers: getHeaders(apiToken),
          params: {
            page: currentPage,
            pageSize: maxPageSize,
            ...(sortBy && { order_by: sortBy }),
            ...(searchQuery && { search: searchQuery }),
            ...(type && { type }),
            ...(category && { category }),
            ...(dietaryRestriction && { dietary_restriction_type: dietaryRestriction })
          },
        });

        allItems = allItems.concat(response.data.data);
        totalPages = response.data.totalPages;
        currentPage++;
      } while (currentPage <= totalPages);

      const exportData = allItems;

      // Helper function to escape CSV values
      const escapeCsvValue = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        // If value contains comma, quote, or newline, wrap in quotes and escape quotes
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      // Create CSV content
      const headers = ['item_id', 'reference_id', 'name', 'type', 'category', 'dietary_restriction_type', 'item_cost', 'default_price'];
      const csvContent = [
        headers.join(','),
        ...exportData.map(item => [
          escapeCsvValue(item.item_id),
          escapeCsvValue(item.reference_id),
          escapeCsvValue(item.name),
          escapeCsvValue(item.type),
          escapeCsvValue(item.category),
          escapeCsvValue(item.dietary_restriction_type),
          escapeCsvValue(item.item_cost),
          escapeCsvValue(item.default_price)
        ].join(','))
      ].join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `items_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleErrorResponse(error, navigate, setShowError, setErrorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const pageTitle = routePageTitle ? routePageTitle : 'Items';

  const columns = [
    { id: 'reference_id', name: 'ID', textColor: 'text-gray-900' },
    { id: 'name', name: 'Name', textColor: 'text-gray-900' },
    { id: 'type', name: 'Category / Type', textColor: 'text-gray-900' },
    { id: 'category', name: 'Subcategory', textColor: 'text-gray-900' },
    { id: 'dietary_restriction_type', name: 'Dietary Restriction Type', textColor: 'text-gray-900' },
    { id: 'item_cost', name: 'Item Cost', textColor: 'text-gray-900' },
    { id: 'default_price', name: 'Default Price', textColor: 'text-gray-900' },
    {
      id: 'edit', name: 'Edit', textColor: 'text-custom-blue', tooltip: 'Edit Item',
      link: (value) => `/items/${value.item_id}`, icon: PencilSquareIcon
    },
    {
      id: 'delete',
      name: '',
      tooltip: 'Delete Item',
      render: (rowData) => (
        <button onClick={() => handleDelete(rowData)} title="Delete Item">
          <TrashIcon className="h-5 w-5 text-red-500" />
        </button>
      )
    }
  ];

  return (
    <MasterLayout pageTitle={pageTitle} searchQuery={searchQuery}>
      {showError && <ErrorMessage message={errorMessage} />}
      <div className="px-2 sm:px-3 lg:px-3">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
          <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
          <div className="flex gap-2">
            <GeneralButton type="button" buttonType="secondary-sm" onClick={handleExport}>
              Export Items
            </GeneralButton>
            <GeneralButton type="submit" buttonType="primary-sm" onClick={() => navigate('/items/create')}>
              Create Item
            </GeneralButton>
          </div>
        </div>
        <ItemFilterComponent 
          onFilterChange={(type, category, dietaryRestriction) => {
            setType(type);
            setCategory(category);
            setDietaryRestriction(dietaryRestriction);
          }} 
          selectedType={type}
          selectedCategory={category}
          selectedDietaryRestriction={dietaryRestriction}
        />
        <div className="inline-block min-w-full py-0 align-middle sm:px-6 lg:px-2">
          <table className="min-w-full divide-y divide-gray-300">
            <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
            <tbody className="divide-y divide-gray-200 bg-white">
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    <Spinner minHeight={630} />
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item) => (
                  <GeneralizedTableRow key={item.item_id} rowData={item} columns={columns} />
                ))
              ) : (
                <NoResultsFound message={"No " + pageTitle.toLocaleLowerCase() + " found"} minHeight={630} />
              )}
            </tbody>
          </table>
          {!isLoading && (
            <PaginationControl
              page={page}
              recordsPerPage={pageSize}
              totalRecords={totalRecords}
              totalPages={totalPages}
              handlePageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </MasterLayout>
  );
}

export default ItemList;
